import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import useHandleRedirect from '../../Hooks/useHandleRedirect';
import { ButtonSecondary } from '../Buttons/button-component';
import MediaComponent from '../Media/media-component.js';
import SystemDataService from '../../Services/SystemDataService';
import * as authActions from '../../Redux/Auth/AuthActions';

/**
 * Simple, flat mobile navigation. A fullscreen dark panel with the site's
 * top-level links as large tappable rows, the WhatsApp CTA and (when relevant)
 * the user's account controls. No multi-level drilling, every link goes
 * straight to its page.
 */
const ModalNavbar = ({ formatter, onDark = false }) => {
    const [open, setOpen] = useState(false);
    const handleRedirect = useHandleRedirect();
    const dispatch = useDispatch();
    const location = useLocation();

    const systemData = useSelector((state) => state.systemData || {});
    const { isAuthenticated, user } = useSelector((state) => state.auth);
    const { dir = 'rtl', links = [], businessInfo = {} } = systemData;
    const isRtl = dir === 'rtl';

    const isAdmin = user?.role === 'admin';
    const displayName = user?.name || user?.email || '';
    const initials = displayName.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase() || (isAdmin ? 'AD' : 'ME');
    const dashboardPath = isAdmin ? '/admin' : '/home';
    const dashboardLabel = isAdmin ? (isRtl ? 'ניהול' : 'Admin Panel') : (isRtl ? 'האזור שלי' : 'My Account');

    const whatsApp = SystemDataService.byLabel(links, 'WhatsApp');
    // Prefer the per-client businessInfo logo; fall back to the links-based logo.
    const bizLogo = businessInfo?.mainLogo || businessInfo?.logo;
    const logoLink = bizLogo
        ? { image: bizLogo }
        : (SystemDataService.byLabel(links, 'Logo Full White') || SystemDataService.byLabel(links, 'Logo Full') || SystemDataService.byLabel(links, 'Logo'));

    // Flatten the formatter down to just the top-level links.
    const navItems = useMemo(() => {
        const items = Array.isArray(formatter) ? formatter : formatter?.elements || [];
        return items.map((it) => ({ id: it.id ?? it.value, name: it.name || it.label, value: it.value || it.href }));
    }, [formatter]);

    // Scroll lock while open.
    useEffect(() => {
        const root = document.documentElement;
        root.style.overflow = open ? 'hidden' : '';
        document.body.style.overflow = open ? 'hidden' : '';
        return () => { root.style.overflow = ''; document.body.style.overflow = ''; };
    }, [open]);

    const close = useCallback(() => setOpen(false), []);

    const isActive = (value) => (value === '/' ? location.pathname === '/' : location.pathname.startsWith(value));

    const go = (value) => {
        handleRedirect({ preventDefault: () => { } }, value);
        close();
    };

    return (
        <div className="flex items-center" dir={dir}>
            {/* Trigger */}
            <button
                className="relative z-[70] p-1 active:scale-90 transition-transform"
                onClick={() => setOpen((v) => !v)}
                aria-label="menu"
            >
                {open ? (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor" className="h-8 w-8 text-white">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                ) : (
                    <svg className={`h-7 w-7 fill-current ${onDark ? 'text-white' : 'text-primary-500'}`} viewBox="0 0 24 24">
                        <path fillRule="evenodd" d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
                    </svg>
                )}
            </button>

            {/* Overlay */}
            <div
                className={`fixed inset-0 w-screen h-[100dvh] bg-primary-950 z-[60] flex flex-col transition-opacity duration-300 ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
            >
                {/* Links */}
                <nav className="flex-1 overflow-y-auto px-8 pt-28 pb-8 flex flex-col items-start gap-1">
                    {navItems.map((item) => (
                        <button
                            key={item.id}
                            onClick={() => go(item.value)}
                            className={`w-full text-start py-3 text-3xl font-primary transition-colors ${isActive(item.value) ? 'text-secondary-400 font-bold' : 'text-white/85 hover:text-white'}`}
                        >
                            {item.name}
                        </button>
                    ))}
                </nav>

                {/* Footer: whatsapp + account */}
                <div className="px-8 pb-10 pt-6 border-t border-white/10 flex flex-col gap-5">
                    {whatsApp?.url && (
                        <ButtonSecondary
                            image={whatsApp.image}
                            size="lg"
                            href={whatsApp.url}
                            className="w-full !py-4 !rounded-full text-sm font-bold font-primary"
                        >
                            {whatsApp.description}
                        </ButtonSecondary>
                    )}

                    {isAuthenticated ? (
                        <div className="w-full rounded-2xl border border-white/10 bg-primary-900 overflow-hidden">
                            <div className="flex items-center gap-3 px-4 py-4 border-b border-white/10">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0 ${isAdmin ? 'bg-red-500' : 'bg-secondary-500'}`}>
                                    {initials}
                                </div>
                                <div className="flex-1 min-w-0">
                                    {user?.name && <p className="text-sm font-bold text-white truncate font-primary">{user.name}</p>}
                                    <p className="text-[11px] text-white/50 truncate font-primary">{user?.email}</p>
                                </div>
                            </div>
                            <div className="flex">
                                <button
                                    onClick={() => go(dashboardPath)}
                                    className="flex-1 py-3 text-[13px] font-bold font-primary text-white hover:bg-white/10 transition-colors border-e border-white/10 text-center"
                                >
                                    {dashboardLabel}
                                </button>
                                <button
                                    onClick={() => { dispatch(authActions.logout()); close(); }}
                                    className="flex-1 py-3 text-[13px] font-bold font-primary text-red-500 hover:bg-red-500/10 transition-colors text-center"
                                >
                                    {isRtl ? 'התנתקות' : 'Sign Out'}
                                </button>
                            </div>
                        </div>
                    ) : (
                        logoLink?.image && (
                            <div className="flex justify-center">
                                <MediaComponent src={logoLink.image} useSekeleton={false} alt="" wrapperClassName="h-9 max-w-[140px] opacity-30" objectFit="contain" />
                            </div>
                        )
                    )}
                </div>
            </div>
        </div>
    );
};

export default React.memo(ModalNavbar);
